/* eslint-disable unused-imports/no-unused-vars */
import React from 'react';
import {
  string,
  shape,
  arrayOf,
  bool,
  number,
} from 'prop-types';
import { Helmet } from 'react-helmet';
import withApplicationConfig from 'containers/withApplicationConfig';
import { getLastUpdateDateSeoFormat } from 'helpers/DateFormatUtil';
import { getStructuredJson, getStructuredFaqJson } from 'helpers/SeoUtils';
/**
 * Uses Helmet to spit out the link and meta tags in
 * the head.
 */
const SeoHead = ({
  canonicalLink,
  image = {},
  metaDescription,
  metaTitle,
  robotsMetaDirectives,
  title,
  preloads,
  preconnects,
  googleSiteVerification,
  microsoftSiteVerification,
  siteName,
  xIcon,
  appleTouchIcon180,
  iconPng32,
  iconPng16,
  manifest,
  msApplicationTileColor,
  themeColor,
  noIndex,
  compositeUpdatedAt,
  verticalName,
  structuredData,
  excludeStructuredData,
  faqs,
  faqSeoIndexes,
}) => (
  <Helmet>
      {/* eslint-disable-next-line */}
      {preconnects && preconnects.links && preconnects.links.map((p, index) => <link rel="preconnect" key={index} href={p} />)}
      {/* eslint-disable-next-line */}
      {preloads && preloads.links && preloads.links.map((p, index) => <link key={index} rel="preload" href={p} as="font" crossOrigin="anonymous" />)}
      <title>{metaTitle}</title>
      <meta name="title" content={metaTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : robotsMetaDirectives} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta name="og:image" content={image && image.url} />
      <meta name="og:image:url" content={image && image.url} />
      <meta name="og:image:secure" content={image && image.url} />
      <link rel="canonical" href={canonicalLink} />
      <link rel="shortcut icon" href={xIcon} type="image/x-icon" />
      <link rel="icon" href={xIcon} type="image/x-icon" />
      <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon180} />
      <link rel="icon" type="image/png" sizes="32x32" href={iconPng32} />
      <link rel="icon" type="image/png" sizes="16x16" href={iconPng16} />
      <link rel="manifest" href={manifest} />
      <meta name="msapplication-TileColor" content={msApplicationTileColor} />
      <meta name="theme-color" content={themeColor} />
      {
      !excludeStructuredData && (
      <script type="application/ld+json">
        {
          `${getStructuredJson(getLastUpdateDateSeoFormat(compositeUpdatedAt),
            canonicalLink, metaTitle, verticalName, metaDescription, structuredData)}`
        }
      </script>
      )
    }
      {faqs && (<script type="application/ld+json">{`${getStructuredFaqJson({ faqs, faqSeoIndexes })}`}</script>)}

      {googleSiteVerification && googleSiteVerification.map(m => <meta key={m} name="google-site-verification" content={m} />)}
      {microsoftSiteVerification && microsoftSiteVerification.map(m => <meta key={m} name="msvalidate.01" content={m} />)}
    </Helmet>
);

SeoHead.defaultProps = {
  title: '',
  metaTitle: '',
  metaDescription: '',
  image: {},
  robotsMetaDirectives: string,
  canonicalLink: string,
  preloads: null,
  preconnects: null,
  googleSiteVerification: [],
  microsoftSiteVerification: [],
  siteName: '',
  xIcon: '',
  appleTouchIcon180: '',
  iconPng32: '',
  iconPng16: '',
  manifest: '',
  msApplicationTileColor: '',
  themeColor: '',
  noIndex: false,
  compositeUpdatedAt: '',
  verticalName: '',
  structuredData: null,
  excludeStructuredData: true,
};

SeoHead.propTypes = {
  title: string,
  metaTitle: string,
  metaDescription: string,
  image: shape({
    url: string.isRequired,
    description: string,
  }),
  robotsMetaDirectives: string,
  canonicalLink: string,
  preloads: shape({}),
  preconnects: shape({}),
  googleSiteVerification: arrayOf(string),
  microsoftSiteVerification: arrayOf(string),
  siteName: string,
  xIcon: string,
  appleTouchIcon180: string,
  iconPng32: string,
  iconPng16: string,
  manifest: string,
  msApplicationTileColor: string,
  themeColor: string,
  noIndex: bool,
  compositeUpdatedAt: string,
  verticalName: string,
  structuredData: shape({}),
  excludeStructuredData: bool,
  faqs: arrayOf(shape({
    question: string.isRequired,
    answer: string.isRequired,
  }).isRequired),
  faqSeoIndexes: arrayOf(number),
};

export default withApplicationConfig(SeoHead, ['structuredData', 'performanceHead', 'siteVerification', 'favicons']);
